/* eslint-disable max-lines */
import { useCurrentCast } from '@/modules/casts/compositions'
import useReplayObjects from './replayObjects'
import store from '@/store'
import { DEFAULT_CLIP_DURATION, IngestClipStatus, IngestCommand, REPLAY_PLAYLIST_ALL_CLIPS } from '../types'
import { ReplayClip } from '../classes'
import useReplayPlaylists from './replayPlaylists'
import useReplayIngests from './replayIngests'
import { PartialRequired } from '@/types'
import useReplayIngestStatusWatcher from './replayIngestStatusWatcher'
import { Result, ResultVoid } from '@/modules/errorHandling/results'
import KisweDate from '@/modules/common/classes/KisweDate'

// Segment size of HLS video segments = possible delay between first frame and video_start_time
const VIDEO_SEGMENT_DURATION_IN_S = 2
// Empirically observed that HLS stream starts earlier than what video_start_time indicates. Adjusting for that
const EXTRA_TIMING_WIGGLE_IN_S = 6.5
// Default extra duration padded to start to ensure start clip does not miss content due to small timing differences
const DEFAULT_CREATE_START_MARGIN_IN_S = 2

// eslint-disable-next-line max-lines-per-function
const useReplayClips = () => {

  const { currentCastId, currentCast } = useCurrentCast()
  const { recordedSources, replayClips, replayPlaylists, downloadableClips } = useReplayObjects(currentCastId)
  const { addClipsToNamedPlaylist, getPlaylistByName, removeClipsFromPlaylist,
    removeClipsFromNamedPlaylist } = useReplayPlaylists()
  const { addClipsToIngest, removeClipsFromIngest, updateClipsInIngests, recreateIngest,
    setIngestCommand } = useReplayIngests()
  const ingestStatusWatcher = useReplayIngestStatusWatcher()

  // Adds a clip from a specific mapped source. Also creates/updates playlist using given tags as playlist name.
  // Updates corresponding ingest
  // end: Playhead location marker for end of clip (seconds)
  // duration: Duration of clip (seconds). Defaults to 30 seconds
  // Assumption: All recorded source start exactly at cast.video_start_time, marking the 0 base for all clips
  // eslint-disable-next-line complexity
  const createClipFromSource = async (params: { sourceId: string, end?: number, duration?: number, tags?: Set<string>,
    startMargin?: number }): Promise<string>  =>
  {
    const recordedSource = recordedSources.value[params.sourceId]
    if (!recordedSource) throw new Error (`Failed to add clip. Recorded source: ${params.sourceId} not found`)
    if (!recordedSource.inputStreamId) {
      throw new Error (`Failed to add clip. Recorded source: ${params.sourceId} is not mapped to input`)
    }
    if (!store.state.events.currentCast?.video_start_time) {
      throw new Error('Failed to add clip. Recording source not started yet')
    }

    const clipName = recordedSource.name ?? ''
    const clipEnd = params.end ?? (Date.now() / 1000 - store.state.events.currentCast?.video_start_time
      - VIDEO_SEGMENT_DURATION_IN_S - EXTRA_TIMING_WIGGLE_IN_S)
    const duration = params.duration ?? DEFAULT_CLIP_DURATION
    const tags = params.tags ?? new Set<string>()
    const startMargin = params.startMargin ?? DEFAULT_CREATE_START_MARGIN_IN_S
    const clipStart = (clipEnd - duration - startMargin > 0) ? clipEnd - duration - startMargin : 0

    const clipIds = await store.dispatch.replayConfig.addReplayClips([{ sourceId: params.sourceId, start: clipStart,
      end: clipEnd, name: clipName, thumbnailTime: clipStart, tags: tags }])

    // Create/update playlists & corresponding ingest - default playlist + one for each tag
    const clip: ReplayClip = { id: clipIds[0], sourceId: params.sourceId, start: clipStart, end: clipEnd,
      name: clipName, thumbnailUrl: null, thumbnailTime: clipStart, tags }
    let playlistId = await addClipsToNamedPlaylist({ name: REPLAY_PLAYLIST_ALL_CLIPS, clipIds })
    await addClipsToIngest({ ingestId: playlistId, clips: [clip] })
    for (const tag of tags) {
      playlistId = await addClipsToNamedPlaylist({ name: tag, clipIds })
      await addClipsToIngest({ ingestId: playlistId, clips: [clip] })
    }

    return clipIds[0]
  }

  // Adds a clip from a set of mapped sources. Clip end marker is assumed to be the current playhead of each source.
  // Also creates/updates playlist using given tags as playlist names. Updated corresponding ingest
  // duration: Duration of clip (seconds). Will be same for all clips created
  // Assumption: All recorded source start exactly at cast.video_start_time, marking the 0 base for all clips
  // eslint-disable-next-line complexity
  const createClipFromSources = async (params: { sourceIds?: string[], duration?: number, tags?: Set<string>,
    startMargin?: number }): Promise<string[]> =>
  {
    if (!store.state.events.currentCast?.video_start_time) {
      throw new Error('Failed to add clip. Recording source not started yet')
    }
    const sourceIds = params.sourceIds ?? Object.keys(recordedSources.value)
    const tags = params.tags ?? new Set<string>()
    const duration = params.duration ?? DEFAULT_CLIP_DURATION
    const clipEnd = (Date.now() / 1000 - store.state.events.currentCast?.video_start_time
      - VIDEO_SEGMENT_DURATION_IN_S - EXTRA_TIMING_WIGGLE_IN_S)
    const startMargin = params.startMargin ?? DEFAULT_CREATE_START_MARGIN_IN_S
    const clipStart = (clipEnd - duration - startMargin > 0) ? clipEnd - duration - startMargin : 0

    const clipList: Omit<ReplayClip, 'id'|'thumbnailUrl'>[] = []
    for (const id of sourceIds) {
      const recordedSource = recordedSources.value[id]
      if (!recordedSource) throw new Error (`Failed to add clip. Recorded source: ${id} not found`)
      if (!recordedSource.inputStreamId) continue

      const newClip: Omit<ReplayClip, 'id'|'thumbnailUrl'> = {
        sourceId: id, start: clipStart, end: clipEnd, name: recordedSource.name ?? '', thumbnailTime: clipStart,
        tags: tags
      }
      clipList.push(newClip)
    }
    if (clipList.length === 0) return []
    const clipIds = await store.dispatch.replayConfig.addReplayClips(clipList)

    // Create/update playlists
    const clips: ReplayClip[] = clipList.map((clip, idx) => ({ id: clipIds[idx], sourceId: clip.sourceId,
      start: clip.start, end: clip.end, name: clip.name, thumbnailUrl: null, thumbnailTime: clip.thumbnailTime,
      tags: clip.tags }))
    let playlistId = await addClipsToNamedPlaylist({ name: REPLAY_PLAYLIST_ALL_CLIPS, clipIds })
    await addClipsToIngest({ ingestId: playlistId, clips })
    for (const tag of tags) {
      playlistId = await addClipsToNamedPlaylist({ name: tag, clipIds })
      await addClipsToIngest({ ingestId: playlistId, clips })
    }

    return clipIds
  }

  // Add the given tag to set of clips. Existing tags are retained. Updated relevant playlists & ingests
  // Assumes clip list in replay config and replay ingest are in sync
  const addTagsToClips = async (params: { clipIds: string[], tags: string[] }): Promise<string[]> => {
    const clipList = []
    const playlistIds: string[] = []
    const clips: ReplayClip[] = []
    for (const id of params.clipIds) {
      const replayClip = replayClips.value[id]
      if (!replayClip) throw new Error (`Failed to add tags to clip: ${id}. Clip not found`)
      clips.push(replayClip)

      const updatedTags = new Set(...replayClip.tags, ...params.tags)
      clipList.push({ id, tags: updatedTags })
    }
    if (clipList.length > 0) {
      await store.dispatch.replayConfig.updateReplayClips(clipList)
      // Create/update playlists & ingest - one for each tag
      for (const tag of params.tags) {
        const clipsWithoutTag = clips.filter((clip) => !clip.tags.has(tag))
        if (clipsWithoutTag.length > 0 ) {
          const clipIds = clipsWithoutTag.map((clip) => clip.id)
          const playlistId = await addClipsToNamedPlaylist({ name: tag, clipIds })
          await addClipsToIngest({ ingestId: playlistId, clips: clipsWithoutTag })
          playlistIds.push(playlistId)
        }
      }
    }
    return playlistIds
  }

  // Remove given tag from set of clips. Remaining tags are retained. Updates corresponding playlist & ingests
  // Assumes clip list in replay config and replay ingest are in sync
  const removeTagsFromClips = async (params: { clipIds: string[], tags: string[] }): Promise<void> => {
    const clipList = []
    for (const id of params.clipIds) {
      const replayClip = replayClips.value[id]
      if (!replayClip) throw new Error (`Failed to remove tags from clip: ${id}. Clip not found`)

      const updatedTags = new Set(...replayClip.tags)
      params.tags.forEach((tag) => updatedTags.delete(tag))
      clipList.push({ id, tags: updatedTags })
    }
    if (clipList.length > 0) await store.dispatch.replayConfig.updateReplayClips(clipList)

    // Remove clip from playlists
    for (const tag of params.tags) {
      const playlist = getPlaylistByName(tag)
      if (playlist) {
        await removeClipsFromPlaylist({ playlistId: playlist.id, clipIds: params.clipIds })
        await removeClipsFromIngest({ ingestId: playlist.id, clipIds: params.clipIds })
      }
    }
  }

  // Updates a replay clip, replacing all attributes with provided attributes. Updates relevant playlist & ingests
  // eslint-disable-next-line complexity
  const updateClip = async (params: Omit<PartialRequired<ReplayClip, 'id'>, 'tags'> & { tags?: string[] }):
    Promise<void> =>
  {
    const updateInfo: PartialRequired<ReplayClip, 'id'>
      = { id: params.id }
    const clip = replayClips.value[params.id]
    if (!clip) throw new Error(`Clip updated failed. Clip: ${params.id} not found.`)

    if (params.start !== undefined) updateInfo.start = params.start
    if (params.end !== undefined) updateInfo.end = params.end
    if (params.thumbnailUrl !== undefined) updateInfo.thumbnailUrl = params.thumbnailUrl
    if (params.tags !== undefined) updateInfo.tags = new Set(params.tags)
    if (params.name !== undefined) updateInfo.name = params.name

    await store.dispatch.replayConfig.updateReplayClips([updateInfo])

    // Remove clip from playlists mapped to tags removed from clip
    const deletedTags = [...clip.tags].filter((tag) => !params.tags?.includes(tag))
    for (const tag of deletedTags) await removeClipsFromNamedPlaylist({ name: tag, clipIds: [clip.id] })

    // Update ingests
    const updatedClip: ReplayClip = {
      id: clip.id,
      sourceId: clip.sourceId,
      start: params.start ?? clip.start,
      end: params.end ?? clip.end,
      name: params.name ?? clip.name,
      thumbnailUrl: params.thumbnailUrl ?? clip.thumbnailUrl,
      thumbnailTime: params.thumbnailTime ?? clip.thumbnailTime,
      tags: params.tags ? new Set(params.tags) : clip.tags
    }
    const removeFromPlaylistIds: string[] = []
    for (const playlist of Object.values(replayPlaylists.value)) {
      if (deletedTags.includes(playlist.name)) removeFromPlaylistIds.push(playlist.id)
    }
    await updateClipsInIngests({ clips: [updatedClip], removeFromPlaylistIds })
  }

  // Deletes the given replay clip, as well as its references from all playlists. Updates ingests. Marks MP4 deleted.
  // Assumes clip list in replay config and replay ingest are in sync
  const deleteClip = async (clipId: string): Promise<void> => {
    const replayClip = replayClips.value[clipId]
    if (!replayClip) {
      console.warn(`Failed to delete clip: ${clipId}. Clip not found. Ignoring`)
      return
    }
    await store.dispatch.replayConfig.deleteReplayClip(clipId)

    for (const playlistId of Object.keys(store.state.castReplays.ingests)) {
      await removeClipsFromIngest({ ingestId: playlistId, clipIds: [clipId] })
    }

    const downloadableClip = downloadableClips.value[clipId]
    if (downloadableClip ) {
      await store.dispatch.events.deleteClip({
        clipId: `clip${downloadableClip.taskTime}`,
        cast: currentCastId.value ?? ''
      })
    }
  }

  // Move (reorder) a clip in a list from current index to a new index. Update ingest
  // Assumes clip list in replay config and replay ingest are in sync
  const setClipIndexInList = async (params: { playlistId: string, fromIdx: number, toIdx: number }): Promise<void> => {
    const playlist = replayPlaylists.value[params.playlistId]
    if (!playlist) throw new Error(`Clip reorder failed. Playlist: ${params.playlistId} not found`)

    const listLen = playlist.clipIdList.length
    if (params.fromIdx < 0 || params.fromIdx >= listLen) {
      throw new Error(`Clip reorder failed. From Index: ${params.fromIdx} must be in [0,${listLen-1}]`)
    }
    if (params.toIdx < 0 || params.toIdx >= listLen) {
      throw new Error(`Clip reorder failed. To Index: ${params.toIdx} must be in [0,${listLen-1}]`)
    }

    const updatedClipIdList = [...playlist.clipIdList]
    updatedClipIdList.splice(params.toIdx, 0, ...updatedClipIdList.splice(params.fromIdx,1))
    await store.dispatch.replayConfig.updateReplayPlaylist({ id: params.playlistId, clipIdList: updatedClipIdList })
    await recreateIngest({ ingestId: params.playlistId, refClipIds: updatedClipIdList,
      chainClips: playlist.chainClips })
  }

  // Start a watcher to trigger ingest play start once clips on all machines are loaded
  const playClipWhenLoaded = (params: { playlistId: string, clipId: string }): void => {
    ingestStatusWatcher.doOnIngestStatus({
      ingestId: params.playlistId,
      condition: (ingestStatus) => (ingestStatus.replay_status[params.clipId]?.status
        === IngestClipStatus.LOADED),
      action: async () => { await setIngestCommand ({ ingestId: params.playlistId,
        command: IngestCommand.START, clipId: params.clipId }) }
    })
  }

  // Request to create MP4 downloadable version of clip using MP4 creator service
  // startMargin - Additional clip to add before start time (seconds)
  // eslint-disable-next-line complexity
  const createClipForDownload = async (clipId: string, startMargin?: number):
    Promise<ResultVoid<'invalid_params'|'database'|'unknown'>> =>
  {
    const clip = replayClips.value[clipId]
    if (!clip) return Result.fail('invalid_params', `Cannot create clip. Clip ${clipId} does not exist`)

    if (!currentCast.value) {
      return Result.fail('invalid_params', 'Cannot create clip. Cast is missing')
    }

    // Assuming clip.sourceId is in format 'mixerN', where N is an integer
    const streamName = `replaystream${clip.sourceId.split('mixer')[1]}`
    const clipStartLabel = KisweDate.formatOffset(clip.start,'H:mm:ss')
    const clipName = `${clip.name} - ${clipStartLabel} - ${clip.end-clip.start}s`
    const isBackup = currentCast.value.scenesSettings.isBackupStreamsEnabled
    const k360event = isBackup ? currentCast.value.k360_event_id_backup : currentCast.value.k360_event_id
    const backupEventDelay = isBackup && currentCast.value.video_start_time_backup ?
      currentCast.value.video_start_time_backup - currentCast.value.video_start_time : 0
    if (!k360event) {
      const errorMsg = isBackup ?
        'Cast uses backup stream but k360_event_id_backup is undefined': 'k360_event_id is undefined'
      return Result.fail('invalid_params', errorMsg)
    }

    const clipinfo = {
      cast: currentCast.value.id,
      k360event,
      start: clip.start - (startMargin ?? 0) - backupEventDelay,
      end: clip.end - backupEventDelay,
      name: clipName,
      tracks: ['CasterTrack1'],
      stream: streamName,
      clipId
    }
    const result = await store.dispatch.events.makeClip(clipinfo)
    if (!result.isSuccess) {
      console.error('Cannot create clip', result.error, result.message)
    }
    return result
  }

  return {
    createClipForDownload,
    deleteClip,
    addTagsToClips,
    createClipFromSource,
    createClipFromSources,
    playClipWhenLoaded,
    removeTagsFromClips,
    setClipIndexInList,
    updateClip
  }
}

export default useReplayClips
